import { useCallback, useEffect, useRef, useState } from "react";
import { getJobProgress } from "../mock/estimateService.mock";

/**
 * Starts a server-side job and polls its progress until done.
 *   const job = useBackgroundJob();
 *   const result = await job.run(() => startParseTakeoff(jobId));
 * `job.progress` → { stages, stageIndex, pct, done } for the progress UI.
 */
export default function useBackgroundJob(pollMs = 250) {
  const [progress, setProgress] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const run = useCallback(
    async (startFn) => {
      setRunning(true);
      setError(null);
      setProgress(null);
      try {
        const { runId } = await startFn();
        for (;;) {
          if (!alive.current) return null;
          const p = await getJobProgress(runId);
          if (!alive.current) return null;
          setProgress(p);
          if (p.done) {
            setRunning(false);
            return p.result;
          }
          await new Promise((r) => setTimeout(r, pollMs));
        }
      } catch (e) {
        if (alive.current) {
          setError(e);
          setRunning(false);
        }
        throw e;
      }
    },
    [pollMs]
  );

  const reset = useCallback(() => {
    setProgress(null);
    setError(null);
    setRunning(false);
  }, []);

  return { run, progress, running, error, reset };
}
